import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import {
  ActionList,
  ActionListItemProps,
  Avatar,
  Button,
  Highlight,
  Icon,
  useSidePanel,
} from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { Application, CustomerEntityType } from 'types/customers'
import { CustomerSidePanel } from '../CustomerDetails/CustomerSidePanel'
import { CUSTOMER_STATUS_VERIFIED } from '../helpers/constants'
import {
  customerTypeHighlightMap,
  customerTypeLabelMap,
  statusHighlightTypeMap,
  statusLabelMap,
} from '../utils/utils'

export interface ApplicationItemsProps {
  applications: Application[]
  useV2Only?: boolean
}

export const ApplicationItems = ({ applications, useV2Only }: ApplicationItemsProps) => {
  const navigate = useNavigate()

  const {
    isSidePanelOpen,
    sidePanelData: selectedApplication,
    paging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel<Application>(applications)

  const onOpenSidePanel = useCallback(
    (index: number) => {
      updateSidePanel(index)
      setIsSidePanelOpen(true)
    },
    [updateSidePanel, setIsSidePanelOpen]
  )

  const items: ActionListItemProps[] = useMemo(
    () =>
      applications?.map(({ id, reference, status, type, name }, index) => ({
        transparent: true,
        key: id,
        id,
        testId: `application-${id}`,
        Avatar: <Avatar type="entity" name={name || reference || id} />,
        title: name || reference || id,
        HeaderHighlightLeft: (
          <Highlight
            iconElement={
              <Icon
                name={type === CustomerEntityType.INDIVIDUAL ? 'UserCircle' : 'BuildingOffice'}
                size="small"
              />
            }
            type={customerTypeHighlightMap[type]}
            variant="emphasis"
          >
            {customerTypeLabelMap[type] ?? type}
          </Highlight>
        ),
        ActionHighlight: (
          <Highlight type={statusHighlightTypeMap[status]}>
            {statusLabelMap[status] ?? status}
          </Highlight>
        ),
        ...(status === CUSTOMER_STATUS_VERIFIED && {
          onItemClick: (e: { stopPropagation: () => void }) => {
            e?.stopPropagation()
            void navigate(`${PathNames.LIVE_CUSTOMERS}/${id}`)
          },
        }),
        CustomAction: (
          <Button
            variant="ghost"
            iconElement={<Icon name="DotsThreeVerticalCustom" />}
            onClick={() => onOpenSidePanel(index)}
          />
        ),
      })),
    [applications, navigate, onOpenSidePanel]
  )

  return (
    <>
      <ActionList id="applications-list" items={items} />
      <CustomerSidePanel
        isOpen={isSidePanelOpen}
        application={selectedApplication}
        paging={paging}
        onClose={() => setIsSidePanelOpen(false)}
        useV2Only={useV2Only}
      />
    </>
  )
}
